import { inject, Injectable, Signal, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { SessionStore } from '../core/services/session.store';
import { InterventionsStore } from '../interventions/interventions.store';
import type { PendingInterventionCreateRequest } from '../interventions/models/pending-intervention-create-request';
import { buildPendingDeviceAction } from '../interventions/utils/build-pending-intervention-action';
import { UISocketService } from '../shared/services/ui.socket.service';

import { useDeviceCommandToasts } from './composables/use-device-command-toasts';
import { DeviceDetailStore } from './device-detail.store';
import { DeviceCommand, DeviceExecutionCommand, DeviceId, DevicePropertyValue, FailureType } from './models/Device';
import { DeviceStateUpdateExecutionInfo, Execution, ExecutionInfo, ExecutionStatus } from './models/Execution';
import { SECTION_TYPES } from './models/SectionType';
import { EditedProperty, UpdateDeviceCommand } from './models/UpdateDeviceCommand';
import { DEVICE_GATEWAY } from './services/DeviceGateway';
import { SiteDevicesStore } from './site-devices.store';

export interface SettingUpdate {
    deviceId: DeviceId;
    property: EditedProperty;
    newValue: DevicePropertyValue;
}

interface TrackedSettingUpdate extends DeviceStateUpdateExecutionInfo {
    /** Survives the changes of the entry, which is replaced rather than mutated. */
    key: number;
    label: string;
    reported: boolean;
    interventionEvent?: PendingInterventionCreateRequest;
}

const QUEUED_DELAY = 2 * 60 * 1000;

const isFinished = (status: ExecutionStatus | undefined) =>
    status === ExecutionStatus.COMPLETED || status === ExecutionStatus.FAILED;

/**
 * The commands sent to the devices and their execution, kept up to date by the real-time execution changes: the
 * manual commands (pairing, unpairing, broken sensors removal), the setting updates and the section refreshes.
 * - A manual command or a section refresh is tracked once the backend accepted it. A setting update is tracked as soon
 *   as it is asked, so that the setting shows as updating while it is sent. A job is registered on the socket once the
 *   backend accepted it. A refused command is not tracked, and a refused setting update or section refresh is reported.
 * - A setting update is only sent during an intervention, which logs it once the device applied it.
 * - A setting update stays tracked until its job completed and the device reported the new state, in either order:
 *   the report is what shows its success. A failed one leaves at once. Starting an update forgets the finished ones.
 * - A section refresh is tracked per job, since the states refreshed together share one. Starting it again replaces
 *   the jobs of that section, and the section is done once all of them finished.
 * - A job still queued on the gateway after 2 minutes is reported as queued and counted as failed, unless it finished
 *   in the meantime. A queued setting update is logged as queued, and stays tracked: the device applies it when it
 *   wakes up, and reports it.
 * - A status change for a job that was not sent from here is ignored.
 * - The state lives as long as the page: logging out leaves the application, so there is no session reset.
 */
@Injectable({ providedIn: 'root' })
export class DeviceCommandsStore {
    private readonly deviceGateway = inject(DEVICE_GATEWAY);
    private readonly uiSocket = inject(UISocketService);
    private readonly deviceDetail = inject(DeviceDetailStore);
    private readonly siteDevices = inject(SiteDevicesStore);
    private readonly session = inject(SessionStore);
    private readonly interventions = inject(InterventionsStore);
    private readonly toasts = useDeviceCommandToasts();

    private readonly commands = signal<Execution[]>([]);
    readonly executions = this.commands.asReadonly();

    private readonly updates = signal<TrackedSettingUpdate[]>([]);
    readonly settingUpdates: Signal<DeviceStateUpdateExecutionInfo[]> = this.updates.asReadonly();
    private lastUpdateKey = 0;

    private readonly refreshes = signal<ExecutionInfo[]>([]);
    readonly sectionRefreshes = this.refreshes.asReadonly();

    async execute(deviceId: DeviceId, command: DeviceExecutionCommand): Promise<Execution> {
        const execution = await firstValueFrom(this.deviceGateway.executeCommand(deviceId, command));
        this.commands.update((executions) => [...executions, execution]);
        // Tell the backend to track the job, so that it reports its changes through the socket.
        this.uiSocket.setExecutions([execution.job_id]);
        return execution;
    }

    /** Resolves with false when nothing was sent, or the backend refused it. */
    async updateSetting({ deviceId, property, newValue }: SettingUpdate): Promise<boolean> {
        const device = this.deviceDetail.device(deviceId)?.rawData;
        const sessionId = device && this.interventions.opened(device.site_id)?.sessionId;
        if (!device || !sessionId) return false;

        const update: TrackedSettingUpdate = {
            key: ++this.lastUpdateKey,
            job_id: '',
            siteId: device.site_id,
            deviceId,
            state: property.id,
            status: ExecutionStatus.INITIALIZED,
            label: property.label,
            reported: false,
        };
        this.updates.update((updates) => [
            ...updates.filter((u) => !isFinished(u.status) && (u.deviceId !== deviceId || u.state !== property.id)),
            update,
        ]);

        const command: UpdateDeviceCommand = { device, property, newValue, sessionId };
        let execution: Execution;
        try {
            execution = await firstValueFrom(this.deviceGateway.updateDevice(command));
        } catch {
            this.removeUpdate(update.key);
            this.toasts.settingRefused(device.site_id, device.name, property.label);
            return false;
        }

        this.changeUpdate(update.key, {
            job_id: execution.job_id,
            status: execution.status ?? update.status,
            interventionEvent: buildPendingDeviceAction(
                device,
                this.session.signedInInstaller().id,
                execution,
                command,
                this.deviceDetail.uiProperty(deviceId, property.id)?.formatter,
            ),
        });
        this.uiSocket.setExecutions([execution.job_id]);
        return true;
    }

    refreshSettings(deviceId: DeviceId): Promise<void> {
        return this.refresh(deviceId, SECTION_TYPES.SETTINGS, this.deviceDetail.settingsPropertyIds(deviceId));
    }

    refreshDiagnosticSection(deviceId: DeviceId, sectionTitle: string): Promise<void> {
        const section = this.deviceDetail.diagnosticSections(deviceId)?.find((s) => s.title === sectionTitle);
        return this.refresh(deviceId, sectionTitle, section?.propertyIds ?? []);
    }

    /** The device reported these states: the setting updates they complete show their success. */
    statesReported(deviceId: DeviceId, stateNames: string[]): void {
        const reported = this.updates().filter((u) => u.deviceId === deviceId && stateNames.includes(u.state));
        if (reported.length === 0) return;

        this.toasts.settingSaved(reported[0].siteId, deviceId);
        this.updates.update((updates) =>
            updates.flatMap((u) => {
                if (!reported.includes(u)) return [u];
                return isFinished(u.status) ? [] : [{ ...u, reported: true }];
            }),
        );
    }

    /** Returns the updated execution when the job is a manual command sent from here, undefined otherwise. */
    statusChanged(jobId: string, status: ExecutionStatus, failureType?: string): Execution | undefined {
        const refresh = this.refreshes().find((r) => r.job_id === jobId);
        if (refresh) {
            this.refreshStatusChanged(refresh, status, failureType);
            return undefined;
        }

        const update = this.updates().find((u) => u.job_id === jobId);
        if (update) {
            this.settingStatusChanged(update, status, failureType);
            return undefined;
        }

        return this.commandStatusChanged(jobId, status, failureType);
    }

    /** The command of this kind still running on the device, if any. */
    pending(deviceId: DeviceId, command: DeviceCommand): Execution | undefined {
        return this.commands().find((e) => e.deviceId === deviceId && e.state === command && !isFinished(e.status));
    }

    private commandStatusChanged(jobId: string, status: ExecutionStatus, failureType?: string): Execution | undefined {
        const execution = this.commands().find((e) => e.job_id === jobId);
        if (!execution) return undefined;

        const updated: Execution = {
            ...execution,
            status,
            failureType: failureType !== FailureType.NO_FAILURE ? failureType : undefined,
        };
        this.commands.update((executions) => executions.map((e) => (e === execution ? updated : e)));
        return updated;
    }

    private settingStatusChanged(update: TrackedSettingUpdate, status: ExecutionStatus, failureType?: string): void {
        if (status === ExecutionStatus.FAILED) {
            if (failureType !== FailureType.CMDDEPRECATED) {
                this.toasts.settingFailed(update.siteId, update.deviceId, update.label, false);
            }
            this.removeUpdate(update.key);
            return;
        }

        if (status === ExecutionStatus.COMPLETED) {
            if (update.interventionEvent) this.logSettingUpdate(update.interventionEvent);
            if (update.reported) {
                this.removeUpdate(update.key);
                return;
            }
        }

        this.changeUpdate(
            update.key,
            status === ExecutionStatus.COMPLETED ? { status, interventionEvent: undefined } : { status },
        );
        if (status === ExecutionStatus.QUEUED_GATEWAY_SIDE) {
            setTimeout(() => this.settingStillQueued(update.job_id), QUEUED_DELAY);
        }
    }

    private settingStillQueued(jobId: string): void {
        const update = this.updates().find((u) => u.job_id === jobId);
        if (!update || isFinished(update.status)) return;

        this.changeUpdate(update.key, { status: ExecutionStatus.FAILED, interventionEvent: undefined });
        const event = update.interventionEvent;
        if (event) void this.interventions.saveEvent({ ...event, data: { ...event.data, queued: true } });
        this.toasts.settingFailed(update.siteId, update.deviceId, update.label, true);
    }

    private logSettingUpdate(event: PendingInterventionCreateRequest): void {
        void this.interventions.saveEvent(event).then(() => {
            this.deviceGateway.getDeviceById(event.id_device).subscribe((device) => {
                this.siteDevices.deviceUpdated(event.id_site, device);
            });
        });
    }

    private changeUpdate(key: number, change: Partial<TrackedSettingUpdate>): void {
        this.updates.update((updates) => updates.map((u) => (u.key === key ? { ...u, ...change } : u)));
    }

    private removeUpdate(key: number): void {
        this.updates.update((updates) => updates.filter((u) => u.key !== key));
    }

    private async refresh(deviceId: DeviceId, section: string, propertyIds: string[]): Promise<void> {
        const device = this.deviceDetail.device(deviceId)?.rawData;
        if (!device || propertyIds.length === 0) return;

        let executions: Execution[];
        try {
            executions = await firstValueFrom(this.deviceGateway.refreshDeviceStates(deviceId, propertyIds));
        } catch {
            this.toasts.refreshRefused(device.site_id, device.name, section);
            return;
        }

        const jobs = new Map<string, ExecutionInfo>();
        executions.forEach(({ job_id, state, status }) => {
            if (jobs.has(job_id)) return;
            jobs.set(job_id, {
                job_id,
                siteId: device.site_id,
                deviceId,
                section,
                state: state ?? '',
                status: status ?? ExecutionStatus.INITIALIZED,
            });
        });
        this.refreshes.update((refreshes) => [
            ...refreshes.filter((r) => r.deviceId !== deviceId || r.section !== section),
            ...jobs.values(),
        ]);
        this.uiSocket.setExecutions([...jobs.keys()]);
    }

    private refreshStatusChanged(refresh: ExecutionInfo, status: ExecutionStatus, failureType?: string): void {
        if (status === ExecutionStatus.FAILED && failureType !== FailureType.CMDDEPRECATED) {
            this.toasts.refreshFailed(refresh.siteId, refresh.deviceId, this.stateLabel(refresh), false);
        }
        if (status === ExecutionStatus.QUEUED_GATEWAY_SIDE) {
            setTimeout(() => this.refreshStillQueued(refresh.job_id), QUEUED_DELAY);
        }
        this.changeRefresh(refresh, status);
    }

    private refreshStillQueued(jobId: string): void {
        const refresh = this.refreshes().find((r) => r.job_id === jobId);
        if (!refresh || isFinished(refresh.status)) return;

        this.toasts.refreshFailed(refresh.siteId, refresh.deviceId, this.stateLabel(refresh), true);
        this.changeRefresh(refresh, ExecutionStatus.FAILED);
    }

    /** A section is done once all its jobs finished: it succeeded when they all completed. */
    private changeRefresh(refresh: ExecutionInfo, status: ExecutionStatus): void {
        this.refreshes.update((refreshes) => refreshes.map((r) => (r === refresh ? { ...r, status } : r)));

        const section = this.refreshes().filter(
            (r) => r.deviceId === refresh.deviceId && r.section === refresh.section,
        );
        if (!section.every((r) => isFinished(r.status))) return;

        if (section.every((r) => r.status === ExecutionStatus.COMPLETED)) this.toasts.refreshCompleted();
        this.refreshes.update((refreshes) => refreshes.filter((r) => !section.includes(r)));
    }

    private stateLabel({ deviceId, state }: ExecutionInfo): string {
        return this.deviceDetail.uiProperty(deviceId, state)?.label ?? 'unknown';
    }
}
